/**
 * Deterministic, client-side anonymization helpers for presentation mode.
 *
 * Jede Funktion nimmt den Originalwert und liefert einen Ersatzwert, der
 *   - deterministisch ist (gleicher Input → immer gleicher Output),
 *   - die Form/Laenge/Magnitude des Originals grob beibehaelt,
 *   - damit Screenshots & Screen-Shares plausibel aussehen, ohne echte Daten zu verraten.
 *
 * WICHTIG: Es findet keinerlei Server-Kommunikation statt. Die Daten werden lediglich
 * bei der Anzeige ersetzt. Originaldaten verbleiben unveraendert in Backend/DB.
 */

/** FNV-1a 32-bit hash – schnell, stabil, ohne Dependencies. */
function fnv1a(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

function seedFrom(input: string | number | null | undefined, salt = ''): number {
  return fnv1a(String(input ?? '') + '|' + salt)
}

/** Mulberry32-artiger deterministischer RNG aus einem Seed. */
function rng(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// ---------------------------------------------------------------------------
// Namen (Firmen, Personen, Ansprechpartner)
// ---------------------------------------------------------------------------

const COMPANY_ADJECTIVES = [
  'Alpine', 'Nordic', 'Atlantic', 'Pacific', 'Baltic', 'Rheinische', 'Bayerische',
  'Westliche', 'Östliche', 'Süddeutsche', 'Hanseatische', 'Europäische', 'Globale',
  'Digitale', 'Moderne', 'Klassische', 'Innovative', 'Nova', 'Prima', 'Omni',
]

const COMPANY_CORE = [
  'Meridian', 'Zenit', 'Horizont', 'Kristall', 'Quantum', 'Vector', 'Matrix',
  'Nexus', 'Orbit', 'Pulsar', 'Sigma', 'Stellar', 'Titan', 'Atlas', 'Borealis',
  'Aurora', 'Arcadia', 'Phoenix', 'Lumen', 'Terra', 'Fusion', 'Helios', 'Juno',
  'Corvus', 'Lynx', 'Falken', 'Adler', 'Panther', 'Delphin', 'Korall',
]

const COMPANY_DOMAIN = [
  'IT-Systeme', 'Consulting', 'Solutions', 'Services', 'Handels', 'Industrie',
  'Technik', 'Engineering', 'Netzwerk', 'Digital', 'Media', 'Partner',
  'Logistik', 'Projekt', 'Office', 'Werke', 'Gruppe', 'Group',
]

const COMPANY_SUFFIXES = ['GmbH', 'GmbH & Co. KG', 'AG', 'KG', 'UG', 'OHG', 'SE', 'GbR', 'e.K.']

const FIRST_NAMES = [
  'Alex', 'Bernd', 'Carla', 'Dirk', 'Eva', 'Frank', 'Greta', 'Hans', 'Ines',
  'Jörg', 'Katja', 'Lutz', 'Maria', 'Nils', 'Olaf', 'Petra', 'Quentin', 'Rita',
  'Sven', 'Tanja', 'Uwe', 'Vera', 'Wolf', 'Xenia', 'Yves', 'Zora',
]

const LAST_NAMES = [
  'Schmidt', 'Müller', 'Weber', 'Fischer', 'Meyer', 'Wagner', 'Becker', 'Hoffmann',
  'Schäfer', 'Koch', 'Richter', 'Klein', 'Wolf', 'Neumann', 'Schwarz', 'Zimmermann',
  'Braun', 'Krüger', 'Hofmann', 'Hartmann',
]

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length] as T
}

/** Erkennt heuristisch, ob ein Name wie ein Firmenname aussieht. */
function looksLikeCompany(name: string): boolean {
  return /\b(GmbH|AG|KG|UG|OHG|SE|GbR|e\.?K\.?|AB|B\.?V\.?|S\.?A\.?|Ltd|Inc|LLC|Corp|Co\.)\b/i.test(name)
    || /&\s*Co\./i.test(name)
    || name.split(/\s+/).length >= 3
}

export function anonymizeCompanyName(original: string): string {
  if (!original) return original
  const s = seedFrom(original, 'company')
  const r = rng(s)
  const useAdjective = r() < 0.55
  const useDomain = r() < 0.7
  const parts: string[] = []
  if (useAdjective) parts.push(pick(COMPANY_ADJECTIVES, Math.floor(r() * 1e6)))
  parts.push(pick(COMPANY_CORE, Math.floor(r() * 1e6)))
  if (useDomain) parts.push(pick(COMPANY_DOMAIN, Math.floor(r() * 1e6)))
  parts.push(pick(COMPANY_SUFFIXES, Math.floor(r() * 1e6)))
  return parts.join(' ')
}

export function anonymizePersonName(original: string): string {
  if (!original) return original
  const s = seedFrom(original, 'person')
  const r = rng(s)
  const first = pick(FIRST_NAMES, Math.floor(r() * 1e6))
  const last = pick(LAST_NAMES, Math.floor(r() * 1e6))
  return `${first} ${last}`
}

/** Automatisch Firma vs. Person erkennen. */
export function anonymizeName(original: string | null | undefined): string {
  if (original == null) return ''
  const s = String(original).trim()
  if (!s) return s
  return looksLikeCompany(s) ? anonymizeCompanyName(s) : anonymizePersonName(s)
}

// ---------------------------------------------------------------------------
// Betraege & Zahlen
// ---------------------------------------------------------------------------

/** Zielbereich fuer anonymisierte Betraege: 100.000 € – 200.000 €. */
const AMOUNT_MIN_EUR = 100_000
const AMOUNT_MAX_EUR = 200_000

/**
 * Betraege deterministisch umformen.
 *
 * Strategie: Immer POSITIV und immer im Bereich 100.000 €–200.000 €
 * (inkl. deterministischer Cent-Stellen). Damit sehen Kontostaende, OPOS-Salden,
 * Rechnungssummen etc. im Praesentations-Modus immer „solide" aus und es gibt
 * keine negativen Kontostaende / Guthaben, die in Screenshots auffallen wuerden.
 *
 * Null bleibt Null (z. B. leere Zellen, Summen ueber Null-Listen).
 */
export function anonymizeAmount(amount: number | null | undefined): number {
  if (amount == null || !Number.isFinite(amount)) return 0
  if (amount === 0) return 0
  // Seed aus Originalbetrag – gleicher Originalbetrag ⇒ immer gleicher Ersatzwert.
  const seed = seedFrom(Math.abs(amount).toFixed(2), 'amount')
  const r = rng(seed)
  const range = AMOUNT_MAX_EUR - AMOUNT_MIN_EUR
  const euros = AMOUNT_MIN_EUR + Math.floor(r() * range) // ganze Euro im Bereich
  const cents = Math.floor(r() * 100) // 0–99
  return euros + cents / 100
}

/**
 * Ganzzahl anonymisieren (z. B. Stueckzahlen). Behaelt Groessenordnung, keine Nachkommastellen.
 */
export function anonymizeInteger(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value)) return 0
  if (value === 0) return 0
  const sign = Math.sign(value)
  const abs = Math.abs(value)
  const seed = seedFrom(abs.toFixed(0), 'int')
  const r = rng(seed)
  const mult = 0.5 + r() * 1.5
  return sign * Math.max(1, Math.round(abs * mult))
}

// ---------------------------------------------------------------------------
// IBAN / Kontonummern
// ---------------------------------------------------------------------------

/** Erzeugt eine deterministische Pseudo-IBAN mit gleichem Laenderpraefix & Laenge. */
export function anonymizeIban(original: string | null | undefined): string {
  if (!original) return ''
  const raw = String(original).replace(/\s+/g, '').toUpperCase()
  if (raw.length < 6) return raw
  const cc = raw.slice(0, 2).replace(/[^A-Z]/g, '') || 'DE'
  const seed = seedFrom(raw, 'iban')
  const r = rng(seed)
  const digits: string[] = []
  for (let i = cc.length; i < raw.length; i++) {
    digits.push(String(Math.floor(r() * 10)))
  }
  const out = cc + digits.join('')
  // 4er-Gruppen einfuegen, wie bei IBAN ueblich
  return out.match(/.{1,4}/g)?.join(' ') ?? out
}

// ---------------------------------------------------------------------------
// CardCode / Belegnummern / Kontonummern
// ---------------------------------------------------------------------------

/**
 * CardCodes (z. B. „C12345", „K00042", „D1001") – Praefix-Buchstaben bleiben,
 * Ziffern werden deterministisch ersetzt. Laenge bleibt gleich.
 */
export function anonymizeCode(original: string | null | undefined): string {
  if (!original) return ''
  const s = String(original)
  if (!s) return s
  const seed = seedFrom(s, 'code')
  const r = rng(seed)
  return s.replace(/\d/g, () => String(Math.floor(r() * 10)))
}

/** Belegnummern (DocNum, InvoiceNum, RE-Nr.) – reine Ziffernersetzung, Laenge erhalten. */
export function anonymizeDocNumber(original: string | number | null | undefined): string {
  if (original == null) return ''
  return anonymizeCode(String(original))
}

/**
 * Generisches Token: ALLES wird deterministisch ersetzt — Buchstaben durch
 * Buchstaben (Gross/Klein bleibt), Ziffern durch Ziffern. Trennzeichen (.-_@/ )
 * bleiben erhalten, damit die Struktur lesbar bleibt.
 *
 * Ideal fuer:
 *   - AD-Prefix („GEW" → „XQM")
 *   - Domain-Namen („gib-entsorgung.local" → „xqm-abcxyzef.klmo")
 *   - Server-/Hostnamen („srv01.kunde.local" → „aqz03.xyxfo.dfkl")
 *   - Benutzer-/Gruppennamen
 *   - Freie Bezeichner, bei denen nur Ziffernersetzung zu wenig ist.
 */
export function anonymizeToken(original: string | null | undefined): string {
  if (!original) return ''
  const s = String(original)
  const seed = seedFrom(s, 'token')
  const r = rng(seed)
  return s.replace(/./g, (ch) => {
    if (/[0-9]/.test(ch)) return String(Math.floor(r() * 10))
    if (/[A-Z]/.test(ch)) return String.fromCharCode(65 + Math.floor(r() * 26))
    if (/[a-z]/.test(ch)) return String.fromCharCode(97 + Math.floor(r() * 26))
    return ch
  })
}

/**
 * IP-Adresse anonymisieren — erhaelt das Format (IPv4: 4 Oktette, IPv6: Hex-Gruppen).
 * Jedes Oktett wird deterministisch auf einen neuen Wert gemappt.
 */
export function anonymizeIp(original: string | null | undefined): string {
  if (!original) return ''
  const s = String(original).trim()
  // IPv4
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(s)) {
    const seed = seedFrom(s, 'ip')
    const r = rng(seed)
    const parts: number[] = []
    parts.push(10) // immer privates Netz fuer Demo
    parts.push(Math.floor(r() * 256))
    parts.push(Math.floor(r() * 256))
    parts.push(1 + Math.floor(r() * 254))
    return parts.join('.')
  }
  // IPv6 oder anderes Format: Hex-Tokenisierung
  return anonymizeToken(s)
}

/**
 * MAC-Adresse anonymisieren — erhaelt das Format (6 Hex-Paare).
 */
export function anonymizeMac(original: string | null | undefined): string {
  if (!original) return ''
  const sep = String(original).includes(':') ? ':' : String(original).includes('-') ? '-' : ':'
  const seed = seedFrom(String(original).toLowerCase(), 'mac')
  const r = rng(seed)
  const parts: string[] = []
  for (let i = 0; i < 6; i++) {
    parts.push(Math.floor(r() * 256).toString(16).padStart(2, '0'))
  }
  return parts.join(sep)
}

// ---------------------------------------------------------------------------
// E-Mail-Adressen
// ---------------------------------------------------------------------------

export function anonymizeEmail(original: string | null | undefined): string {
  if (!original) return ''
  const s = String(original)
  const at = s.indexOf('@')
  if (at < 0) return s
  const local = s.slice(0, at)
  const domain = s.slice(at + 1)
  const seedL = seedFrom(local, 'email-local')
  const seedD = seedFrom(domain, 'email-domain')
  const rL = rng(seedL)
  const rD = rng(seedD)
  const newLocal =
    pick(FIRST_NAMES, Math.floor(rL() * 1e6)).toLowerCase() +
    '.' +
    pick(LAST_NAMES, Math.floor(rL() * 1e6)).toLowerCase()
  // TLD beibehalten (.de, .com)
  const dotIdx = domain.lastIndexOf('.')
  const tld = dotIdx > 0 ? domain.slice(dotIdx) : '.de'
  const newDomain =
    pick(COMPANY_CORE, Math.floor(rD() * 1e6)).toLowerCase() + tld
  return `${newLocal}@${newDomain}`
}

// ---------------------------------------------------------------------------
// Telefonnummern
// ---------------------------------------------------------------------------

export function anonymizePhone(original: string | null | undefined): string {
  if (!original) return ''
  const s = String(original)
  const seed = seedFrom(s, 'phone')
  const r = rng(seed)
  return s.replace(/\d/g, () => String(Math.floor(r() * 10)))
}

// ---------------------------------------------------------------------------
// Generisch: Freitext-Zeile (Verwendungszweck, Buchungstext)
// ---------------------------------------------------------------------------

const GENERIC_TEXTS = [
  'Rechnungsausgleich',
  'Sammelüberweisung',
  'Dauerauftrag Miete',
  'Wartungsvertrag Q',
  'Hardware-Lieferung',
  'Lizenzgebühr',
  'Servicepauschale',
  'Gutschrift',
  'Teilzahlung',
  'Vorkasse',
  'Auftragsbestätigung',
  'Projektabrechnung',
  'Materialkosten',
  'Beratungsleistung',
]

export function anonymizeFreeText(original: string | null | undefined): string {
  if (!original) return ''
  const s = String(original).trim()
  if (!s) return s
  const seed = seedFrom(s, 'freetext')
  const r = rng(seed)
  const base = pick(GENERIC_TEXTS, Math.floor(r() * 1e6))
  const ref = String(1000 + Math.floor(r() * 9000))
  return `${base} ${ref}`
}
